/**
 * Sino de notificações do cabeçalho.
 * O painel do sino é um diálogo não modal: Esc, clique fora e navegação fecham; o foco volta ao botão.
 */
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { marcarLidas, notificacoesQuery, tempoRelativo, type Notificacao } from '../lib/app';
import type { AbaPainel } from '../lib/painel';
import { IconBell, IconFlag, IconMessage, IconPaperclip, IconSparkle, IconX } from './icons';
import { PushBloco } from './push';
import { ErrorBox, Skeleton } from './ui';

// ---------- sino ----------

const ICONE: Record<Notificacao['tipo'], ReactNode> = {
  comentario: <IconMessage size={15} />,
  etapa: <IconFlag size={15} />,
  edital_aprovado: <IconSparkle size={15} />,
  anexo: <IconPaperclip size={15} />,
};

/** Aba do painel que a notificação abre. */
const ABA: Partial<Record<Notificacao['tipo'], AbaPainel>> = { comentario: 'conversa', anexo: 'anexos' };

const rotuloNaoLidas = (n: number) => (n === 0 ? 'nenhuma não lida' : n === 1 ? '1 não lida' : `${n} não lidas`);

export function Sino() {
  const [aberto, setAberto] = useState(false);
  const [topo, setTopo] = useState(48);
  const btn = useRef<HTMLButtonElement>(null);
  const painel = useRef<HTMLDivElement>(null);
  const tituloId = useId();
  const navigate = useNavigate();
  const q = useQuery(notificacoesQuery);
  const naoLidas = q.data?.naoLidas ?? 0;

  // Fechar: Esc, clique fora; ao fechar o foco volta ao sino.
  useEffect(() => {
    if (!aberto) return;
    painel.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        fechar();
      }
    };
    const onClick = (e: MouseEvent) => {
      const alvo = e.target as Node;
      if (!painel.current?.contains(alvo) && !btn.current?.contains(alvo)) setAberto(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [aberto]);

  const fechar = () => {
    setAberto(false);
    btn.current?.focus();
  };

  const abrir = () => {
    // No celular o painel vira uma folha logo abaixo do cabeçalho inteiro (inclui a navegação compacta).
    const h = btn.current?.closest('header')?.getBoundingClientRect().bottom ?? 48;
    setTopo(Math.max(0, Math.round(h)));
    setAberto(true);
  };

  const abrirItem = async (n: Notificacao) => {
    if (!n.lidaEm) void marcarLidas([n.id]);
    if (!n.fitId) return;
    setAberto(false);
    await navigate({ to: '/editais/$fitId', params: { fitId: n.fitId }, search: { aba: ABA[n.tipo] ?? 'dados' } });
  };

  return (
    <div className="relative shrink-0">
      <button
        ref={btn}
        type="button"
        className="app-btn app-btn-ghost relative size-9 px-0 text-fg-2"
        aria-label={`Notificações, ${rotuloNaoLidas(naoLidas)}`}
        aria-haspopup="dialog"
        aria-expanded={aberto}
        aria-controls={aberto ? `${tituloId}-painel` : undefined}
        onClick={() => (aberto ? fechar() : abrir())}
      >
        <IconBell size={16} />
        {naoLidas > 0 && (
          <span
            aria-hidden="true"
            className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 font-mono text-[10px] leading-none font-semibold text-accent-ink"
          >
            {naoLidas > 9 ? '9+' : naoLidas}
          </span>
        )}
      </button>

      {aberto && (
        <div
          id={`${tituloId}-painel`}
          ref={painel}
          role="dialog"
          aria-labelledby={tituloId}
          tabIndex={-1}
          style={{ '--topo': `${topo}px` } as React.CSSProperties}
          className="fixed inset-x-0 top-[var(--topo)] z-30 flex max-h-[calc(100dvh-var(--topo))] flex-col border-b border-line bg-bg-2 shadow-xl outline-none sm:absolute sm:inset-x-auto sm:top-[calc(100%+6px)] sm:right-0 sm:max-h-[min(520px,calc(100dvh-80px))] sm:w-[380px] sm:rounded-xl sm:border"
        >
          <header className="flex items-center justify-between gap-2 border-b border-line px-4 py-2.5">
            <h2 id={tituloId} className="text-[14px] font-semibold tracking-[-0.2px]">
              Notificações
            </h2>
            <div className="flex items-center gap-1">
              {naoLidas > 0 && (
                <button type="button" className="app-btn app-btn-ghost h-8 min-h-0 text-[12px]" onClick={() => void marcarLidas()}>
                  Marcar todas como lidas
                </button>
              )}
              <button type="button" className="app-btn app-btn-ghost size-8 min-h-0 px-0" aria-label="Fechar" onClick={fechar}>
                <IconX size={14} />
              </button>
            </div>
          </header>
          <div className="overflow-y-auto overscroll-contain">
            {q.isPending ? (
              <div className="flex flex-col gap-2 p-3">
                <Skeleton h={52} />
                <Skeleton h={52} />
                <Skeleton h={52} />
              </div>
            ) : q.isError ? (
              <div className="p-3">
                <ErrorBox>{q.error.message}</ErrorBox>
              </div>
            ) : q.data.itens.length === 0 ? (
              <p className="px-4 py-8 text-center text-[13px] leading-relaxed text-fg-2">Nenhuma notificação por enquanto.</p>
            ) : (
              <ul className="divide-y divide-line">
                {q.data.itens.map((n) => (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => void abrirItem(n)}
                      className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-bg-3 ${n.lidaEm ? 'text-fg-2' : 'text-fg'}`}
                      style={n.lidaEm ? undefined : { background: 'var(--accent-soft)' }}
                    >
                      <span className={`mt-0.5 shrink-0 ${n.lidaEm ? 'text-mute' : 'text-accent'}`}>{ICONE[n.tipo]}</span>
                      <span className="min-w-0 flex-1">
                        <span className={`block truncate text-[13px] ${n.lidaEm ? '' : 'font-medium'}`}>{n.titulo}</span>
                        {n.texto && <span className="mt-0.5 line-clamp-2 block text-[12px] leading-snug text-fg-2">{n.texto}</span>}
                        <span className="mt-1 block text-[11px] text-mute">
                          <time dateTime={n.em}>{tempoRelativo(n.em)}</time>
                          {!n.lidaEm && <span className="sr-only"> · não lida</span>}
                        </span>
                      </span>
                      {!n.lidaEm && <span aria-hidden="true" className="mt-2 size-2 shrink-0 rounded-full bg-accent" />}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <PushBloco />
        </div>
      )}
    </div>
  );
}
